/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import {
  DEFAULT_PAGE_SIZE,
  WaiverType,
  WaiverView,
  WaiversListState,
} from './waiversListTypes';

type UrlParams = Record<string, string | undefined | null>;

const SORT_KEYS = ['threat', 'dateCreated', 'expiration', 'policy', 'scope'];

const MAX_PAGE_SIZE = 100;

/** Each value is percent-encoded before joining, so a name that contains a comma survives. */
const decode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const list = (value: string | undefined | null): string[] =>
  value ? value.split(',').filter(Boolean).map(decode) : [];

const join = (values: string[]): string | undefined =>
  values.map(encodeURIComponent).join(',') || undefined;

const positive = (value: string | undefined | null, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

export function parseWaiversUrl(params: UrlParams): WaiversListState {
  const type: WaiverType = params.type === 'containers' ? 'containers' : 'components';
  return {
    type,
    view: (params.view === 'requested' ? 'requested' : 'existing') as WaiverView,
    status: list(params.status),
    threat: list(params.threat),
    policy: list(params.policy),
    // Container image waivers are never owned by a repository, so the filter does not apply to them.
    repository: type === 'containers' ? [] : list(params.repository),
    q: params.q ?? '',
    sort: params.sort && SORT_KEYS.includes(params.sort) ? params.sort : null,
    dir: params.dir === 'asc' ? 'asc' : 'desc',
    page: positive(params.page, 1),
    pageSize: Math.min(positive(params.pageSize, DEFAULT_PAGE_SIZE), MAX_PAGE_SIZE),
  };
}

/** Defaults are dropped so the URL stays short. */
export function serializeWaiversUrl(state: WaiversListState): UrlParams {
  return {
    type: state.type === 'components' ? undefined : state.type,
    view: state.view === 'existing' ? undefined : state.view,
    status: join(state.status),
    threat: join(state.threat),
    policy: join(state.policy),
    repository: join(state.repository),
    q: state.q || undefined,
    sort: state.sort ?? undefined,
    dir: state.sort && state.dir === 'asc' ? 'asc' : undefined,
    page: state.page > 1 ? String(state.page) : undefined,
    pageSize: state.pageSize !== DEFAULT_PAGE_SIZE ? String(state.pageSize) : undefined,
  };
}

/** Query string for the backend. Lists are sent as repeated parameters. */
export function toListQuery(state: WaiversListState): string {
  const query = new URLSearchParams();
  query.set('type', state.type);
  query.set('view', state.view);
  (['status', 'threat', 'policy', 'repository'] as const).forEach((key) =>
    state[key].forEach((value) => query.append(key, value))
  );
  if (state.q) {
    query.set('q', state.q);
  }
  if (state.sort) {
    query.set('sort', state.sort);
    query.set('dir', state.dir);
  }
  query.set('page', String(state.page));
  query.set('pageSize', String(state.pageSize));
  return query.toString();
}
